import React, { Component } from 'react';
import styled, { keyframes } from 'styled-components';
import { FiX, FiClock } from 'react-icons/fi';
import { C } from '../chrome';
import { formatTimestamp } from '../utils';

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const popIn = keyframes`
  from { opacity: 0; transform: translateY(8px) scale(0.985); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(2, 5, 12, 0.72);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9100;
  padding: 2rem;
  animation: ${fadeIn} 140ms ease-out;
`;

const Box = styled.div`
  background: ${C.surface};
  border: 1px solid ${C.border};
  border-radius: 1.4rem;
  width: 56rem;
  max-width: 100%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow:
    0 24px 48px -12px rgba(0,0,0,0.6),
    0 12px 24px -8px rgba(0,0,0,0.45);
  animation: ${popIn} 180ms cubic-bezier(0.4, 0, 0.2, 1);
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.6rem 2rem;
  border-bottom: 1px solid ${C.border};
`;

const HeadText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const Title = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${C.textPrimary};
`;

const Sub = styled.div`
  font-size: 1.15rem;
  color: ${C.textMuted};
`;

const Close = styled.button`
  background: transparent;
  border: 1px solid ${C.border};
  color: ${C.textMuted};
  cursor: pointer;
  padding: 0.6rem;
  border-radius: 0.6rem;
  display: flex;
  &:hover { color: ${C.textPrimary}; }
`;

const Body = styled.div`
  padding: 1.6rem 2rem 2rem;
  overflow-y: auto;
`;

const Empty = styled.div`
  padding: 3rem 0;
  text-align: center;
  color: ${C.textMuted};
  font-size: 1.2rem;
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 0.7rem;
    top: 1rem;
    bottom: 1rem;
    width: 1px;
    background: ${C.border};
  }
`;

const Item = styled.li`
  position: relative;
  padding-left: 2.4rem;
`;

const Dot = styled.div`
  position: absolute;
  left: 0.3rem;
  top: 0.5rem;
  width: 0.9rem;
  height: 0.9rem;
  border-radius: 50%;
  background: ${C.surface};
  border: 2px solid ${(p) => p.color || C.purple};
`;

const ItemHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.4rem;
`;

const Action = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 1.1rem;
  font-weight: 700;
  color: ${(p) => p.color || C.purple};
  letter-spacing: 0.04em;
`;

const When = styled.span`
  font-size: 1rem;
  color: ${C.textDim};
  font-variant-numeric: tabular-nums;
`;

const Reason = styled.div`
  font-size: 1.2rem;
  color: ${C.textPrimary};
  margin-bottom: 0.4rem;
  line-height: 1.5;
`;

const By = styled.div`
  font-size: 1.05rem;
  color: ${C.textMuted};
`;

const ACTION_META = {
  mark_tc:     { label: 'Marked as tele-caller', color: C.green },
  mark_doctor: { label: 'Reverted to doctor',    color: C.purple },
  align:       { label: 'Aligned',                color: C.green },
  unalign:     { label: 'Unaligned',              color: C.yellow },
};

class AuditLogModal extends Component {
  componentDidMount() {
    document.addEventListener('keydown', this.onEsc);
  }
  componentWillUnmount() {
    document.removeEventListener('keydown', this.onEsc);
  }
  onEsc = (e) => {
    if (e.key === 'Escape' && this.props.open && this.props.onClose) {
      this.props.onClose();
    }
  };

  render() {
    const { open, agentName, audit, loading, onClose } = this.props;
    if (!open) return null;

    return (
      <Overlay onClick={onClose}>
        <Box onClick={(e) => e.stopPropagation()}>
          <Head>
            <HeadText>
              <Title>Audit log</Title>
              <Sub>{agentName || ''}</Sub>
            </HeadText>
            <Close onClick={onClose} aria-label="Close"><FiX size={18} /></Close>
          </Head>

          <Body>
            {loading ? (
              <Empty>Loading…</Empty>
            ) : (audit || []).length === 0 ? (
              <Empty>No history yet for this agent.</Empty>
            ) : (
              <List>
                {audit.map((a) => {
                  const meta = ACTION_META[a.action] || { label: a.action, color: C.purple };
                  return (
                    <Item key={a.id}>
                      <Dot color={meta.color} />
                      <ItemHead>
                        <Action color={meta.color}>
                          <FiClock size={12} /> {meta.label}
                        </Action>
                        <When>{formatTimestamp(a.created_at)}</When>
                      </ItemHead>
                      <Reason>"{a.reason}"</Reason>
                      <By>by {a.changed_by_name || (a.changed_by ? `User #${a.changed_by}` : 'system')}</By>
                    </Item>
                  );
                })}
              </List>
            )}
          </Body>
        </Box>
      </Overlay>
    );
  }
}

export default AuditLogModal;
